import { useState } from "react";

export function explorerAccountUrl(network: string, address: string): string {
  const sub = network === "mainnet" ? "" : `${network}-`;
  return `https://${sub}explorer.multiversx.com/accounts/${address}`;
}

export function explorerTxUrl(network: string, hash: string): string {
  const sub = network === "mainnet" ? "" : `${network}-`;
  return `https://${sub}explorer.multiversx.com/transactions/${hash}`;
}

export function explorerTokenUrl(network: string, tokenId: string): string {
  const sub = network === "mainnet" ? "" : `${network}-`;
  // collection (e.g. SFOXSY-f7a2e6) vs fungible — explorer routes both via /tokens/
  return `https://${sub}explorer.multiversx.com/tokens/${tokenId}`;
}

/** Pull the human-readable ticker out of a token identifier.
 *  "FOXSY-583905" -> "FOXSY"   "SFOXSY-f7a2e6" -> "SFOXSY"   "SFOXSY-f7a2e6-02" -> "SFOXSY" */
export function tokenTicker(id: string): string {
  if (!id) return "";
  const m = id.match(/^([A-Z0-9]+)-[a-f0-9]+/i);
  return m ? m[1] : id;
}

const iconBtnStyle: React.CSSProperties = {
  padding: "0.15rem 0.45rem",
  fontSize: "0.85rem",
  lineHeight: 1,
  background: "transparent",
  color: "var(--text-dim)",
  borderRadius: 4,
  border: "1px solid var(--border)",
};

/** Click-to-copy button. Shows a brief "copied" affirmation. */
export function CopyButton({
  value,
  title = "Copy to clipboard",
}: {
  value: string;
  title?: string;
}) {
  const [done, setDone] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1100);
    } catch {
      /* clipboard unavailable (older browsers / insecure context) */
    }
  }

  return (
    <button
      onClick={copy}
      title={title}
      aria-label={title}
      style={{
        ...iconBtnStyle,
        background: done ? "var(--success)" : "transparent",
        color: done ? "#0e1116" : "var(--text-dim)",
      }}
    >
      {done ? "✓" : "⧉"}
    </button>
  );
}

/** Opens the address/tx/token in the network-aware explorer in a new tab. */
export function ExplorerButton({
  url,
  title = "Open in explorer",
}: {
  url: string;
  title?: string;
}) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={title}
      aria-label={title}
      style={{ ...iconBtnStyle, textDecoration: "none", display: "inline-block" }}
    >
      ↗
    </a>
  );
}

/** Truncated address (mono) + copy + explorer buttons. */
export function AddressChip({
  address,
  network,
  label,
  short = true,
}: {
  address: string;
  network: string;
  label?: string;
  short?: boolean;
}) {
  if (!address) return null;
  const display = short ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;
  return (
    <span className="row" style={{ gap: "0.35rem" }}>
      {label && <span className="muted">{label}</span>}
      <span className="mono" title={address}>
        {display}
      </span>
      <CopyButton value={address} title="Copy address" />
      <ExplorerButton url={explorerAccountUrl(network, address)} title="Open address in explorer" />
    </span>
  );
}

/** Token-id chip with copy + explorer buttons. */
export function TokenChip({
  tokenId,
  network,
  label,
}: {
  tokenId: string;
  network: string;
  label?: string;
}) {
  if (!tokenId) return null;
  return (
    <span className="row" style={{ gap: "0.35rem" }}>
      {label && <span className="muted">{label}</span>}
      <span className="mono">{tokenId}</span>
      <CopyButton value={tokenId} title="Copy token id" />
      <ExplorerButton url={explorerTokenUrl(network, tokenId)} title="Open token in explorer" />
    </span>
  );
}
